import { useEffect, useState } from 'react';
import { useApi } from '@backstage/core-plugin-api';
import { Box, Button, MenuItem, Paper, TextField, Typography } from '@material-ui/core';
import { Alert } from '@material-ui/lab';
import { ColumnIn, RegistryApiError, RegistryTable, UpdateTableRequest, lhRegistryApiRef } from './api';
import { ManifestPrCard } from './ManifestPrCard';
import { OmHistoryCard } from './OmHistoryCard';
import { RegistryColumns } from './RegistryColumns';
import { RegistryHistory } from './RegistryHistory';
import { Seg, shortActor } from './ui';

const TICKET = /^[A-Z]+-[0-9]+$/;
const MONO = "'Roboto Mono', monospace";

type Props = {
  table: RegistryTable;
  readOnly: boolean;
  onClose: () => void;
  /** 목록을 다시 불러온다 (409 충돌 뒤 서버 최신 값을 받기 위해서도) */
  onReload: () => void;
  onSaved: (message: string) => void;
  /** 작성 중인 변경 요약. 빈 문자열이면 저장할 것 없음 — 목록이 이동 전 확인에 쓴다 */
  onDirty: (summary: string) => void;
};

/** 목록 오른쪽 상세: 편집(domain·table_name·collect_yn·비고·컬럼 수정/삭제/추가) → manifest PR → 컬럼 → 변경 이력 */
export const RegistryDetail = ({ table, readOnly, onClose, onReload, onSaved, onDirty }: Props) => {
  const api = useApi(lhRegistryApiRef);
  // 편집을 시작한 version 과 그때 값. 409 뒤 "서버 최신 값 반영" 전까지는 이 version 으로 저장해 다시 충돌하게 둔다
  const [base, setBase] = useState({ version: table.version, domain: table.domain, tableName: table.tableName, collectYn: table.collectYn, note: table.note ?? '' });
  const [domains, setDomains] = useState<string[]>([]);
  const [domain, setDomain] = useState(table.domain);
  const [tableName, setTableName] = useState(table.tableName);
  const [yn, setYn] = useState(table.collectYn);
  const [note, setNote] = useState(table.note ?? '');
  const [ticket, setTicket] = useState('');
  const [reason, setReason] = useState('');
  const [drop, setDrop] = useState<string[]>([]);
  const [add, setAdd] = useState<Record<string, string | null>>({});
  // 기존 컬럼의 타입·collect_yn 수정분 — 원래 값과 다른 컬럼만 담긴다
  const [colEdit, setColEdit] = useState<Record<string, ColumnIn>>({});
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<{ code: string; message: string }>();

  useEffect(() => {
    api.listDomains().then(d => setDomains(d.map(x => x.domain))).catch(e => setError({ code: '', message: e.message }));
  }, [api]);

  const changes = [
    domain !== base.domain && 'domain', tableName !== base.tableName && 'table_name', yn !== base.collectYn && 'collect_yn', note !== base.note && 'note',
  ].filter((x): x is string => !!x);
  const addNames = Object.keys(add);
  // 삭제로 표시한 컬럼의 수정분은 보내지 않는다
  const update = Object.values(colEdit).filter(u => !drop.includes(u.columnName));
  const colChange = drop.length + addNames.length + update.length > 0;
  const tableNameBad = !tableName || /\s/.test(tableName);
  const ticketOk = TICKET.test(ticket);
  const ticketBad = !!ticket && !ticketOk;
  // 컬럼 추가만 티켓이 필수다 (서버 TICKET_REQUIRED). 삭제·수정은 선택
  const saveDisabled = readOnly || busy || !(changes.length || colChange) || !reason.trim() || tableNameBad || ticketBad || (addNames.length > 0 && !ticketOk);
  const parts = [
    ...changes, update.length ? `컬럼 수정 ${update.length}` : '', addNames.length ? `컬럼 +${addNames.length}` : '', drop.length ? `컬럼 −${drop.length}` : '',
  ].filter(Boolean);
  const summary = parts.length ? `변경: ${parts.join(', ')}` : '변경 사항 없음';
  const dirty = [...parts, reason.trim() && '변경 사유', ticket && '근거 티켓'].filter(Boolean).join(', ');
  useEffect(() => onDirty(dirty), [dirty, onDirty]);

  const conflict = error?.code === 'VERSION_CONFLICT' && table.version !== base.version;
  const takeLatest = () => {
    setBase({ version: table.version, domain: table.domain, tableName: table.tableName, collectYn: table.collectYn, note: table.note ?? '' });
    setDomain(table.domain);
    setTableName(table.tableName);
    setYn(table.collectYn);
    setNote(table.note ?? '');
    setError(undefined);
  };

  const save = async () => {
    setBusy(true);
    setError(undefined);
    try {
      const ch: NonNullable<UpdateTableRequest['changes']> = {};
      if (domain !== base.domain) ch.domain = domain;
      if (tableName !== base.tableName) ch.tableName = tableName;
      if (yn !== base.collectYn) ch.collectYn = yn;
      if (note !== base.note) ch.note = note;
      const r = await api.updateTable(table.id, {
        version: base.version,
        changes: changes.length ? ch : undefined,
        columns: colChange
          ? {
              ...(drop.length ? { drop } : {}),
              ...(addNames.length ? { add: addNames.map(columnName => ({ columnName, dataType: add[columnName] })) } : {}),
              ...(update.length ? { update } : {}),
            }
          : undefined,
        reason: reason.trim(),
        ticketKey: ticket || undefined,
      });
      setBase({ version: r.table.version, domain: r.table.domain, tableName: r.table.tableName, collectYn: r.table.collectYn, note: r.table.note ?? '' });
      setDomain(r.table.domain);
      setTableName(r.table.tableName);
      setYn(r.table.collectYn);
      setNote(r.table.note ?? '');
      setTicket('');
      setReason('');
      setDrop([]);
      setAdd({});
      setColEdit({});
      onSaved(`${r.table.tableName} 저장 — version ${r.table.version}`);
    } catch (e) {
      const err = e as RegistryApiError;
      setError({ code: err.code ?? '', message: err.message });
      if (err.code === 'VERSION_CONFLICT') onReload();
    } finally {
      setBusy(false);
    }
  };

  const ro = { InputProps: { readOnly: true }, variant: 'outlined' as const, size: 'small' as const };
  return (
    <Box component="aside" aria-label="선택한 테이블 상세" display="flex" flexDirection="column" style={{ gap: 16 }}>
      <Paper variant="outlined" component="section">
        <Box p={2.5} display="flex" flexDirection="column" style={{ gap: 14 }}>
          <Box display="flex" alignItems="flex-start" style={{ gap: 8 }}>
            <Box flex={1} minWidth={0}>
              <Typography variant="caption" color="textSecondary">{table.domain} · id {table.id} · version {table.version}</Typography>
              <Typography variant="h6" component="h2" style={{ fontFamily: MONO, wordBreak: 'break-all' }}>{table.tableName}</Typography>
            </Box>
            <Button variant="outlined" size="small" aria-label="상세 닫기" onClick={onClose}>닫기</Button>
          </Box>

          {conflict && (
            <Alert severity="warning">
              <b>다른 사용자가 먼저 저장했습니다 (409 VERSION_CONFLICT)</b>
              <br />편집을 시작한 version {base.version} 이후에 <b>{shortActor(table.updatedBy)}</b>님이 version {table.version}을 저장했습니다.
              <Box my={1} style={{ display: 'grid', gridTemplateColumns: 'auto 1fr 1fr', columnGap: 12, rowGap: 2 }}>
                <span />
                <b>서버 최신 (v{table.version})</b>
                <b>내가 편집 중</b>
                <span>domain</span><span>{table.domain}</span><span>{domain}</span>
                <span>table_name</span><span>{table.tableName}</span><span>{tableName}</span>
                <span>collect_yn</span><span>{table.collectYn}</span><span>{yn}</span>
                <span>note</span><span>{table.note || '(없음)'}</span><span>{note || '(없음)'}</span>
              </Box>
              <Box display="flex" alignItems="center" flexWrap="wrap" style={{ gap: 8 }}>
                <Button size="small" variant="outlined" onClick={takeLatest}>서버 최신 값 반영</Button>
                <Typography variant="caption">입력한 변경 사유·티켓은 그대로 유지됩니다.</Typography>
              </Box>
            </Alert>
          )}
          {error?.code === 'JIRA_UNAVAILABLE' && (
            <Alert severity="error">
              <b>Jira에 연결할 수 없습니다 (503 JIRA_UNAVAILABLE)</b>
              <br />티켓을 확인할 수 없어 저장을 막았습니다. 조회와 이력은 그대로 볼 수 있습니다.
            </Alert>
          )}
          {error && !conflict && error.code !== 'JIRA_UNAVAILABLE' && <Alert severity="error">{error.message}</Alert>}
          {readOnly && (
            <Alert severity="info">
              {table.legacy
                ? <><b>읽기 전용</b> — legacy 행은 수정할 수 없습니다 (보존·대조용).</>
                : <><b>읽기 전용</b> — 쓰기 허용 목록(app.adminEmails)에 없는 계정입니다. 변경이 필요하면 담당자에게 요청하세요.</>}
            </Alert>
          )}

          <Box style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 12 }}>
            <TextField id="rd-7" select label="domain" value={domain} onChange={e => setDomain(e.target.value)} disabled={readOnly} variant="outlined" size="small">
              {[...new Set([base.domain, ...domains])].map(d => <MenuItem key={d} value={d}>{d}</MenuItem>)}
            </TextField>
            <TextField id="rd-8"
              label="table_name" value={tableName} onChange={e => setTableName(e.target.value.trim())} disabled={readOnly}
              error={tableNameBad} helperText={tableNameBad ? '공백 없는 이름을 넣으세요' : undefined} variant="outlined" size="small" InputProps={{ style: { fontFamily: MONO } }}
            />
          </Box>
          <Box style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))', gap: 12 }}>
            <TextField id="rd-1" label="원천 시스템" value={table.sourceSystem} {...ro} />
            <TextField id="rd-2" label="원천 유형" value={table.sourceType} {...ro} />
            <TextField id="rd-3" label="원천 테이블" value={table.sourceTableName} {...ro} />
          </Box>
          <Box display="flex" alignItems="center" style={{ gap: 12 }}>
            <Typography variant="body2" color="textSecondary">수집 여부</Typography>
            <Seg value={yn} options={[['Y', 'Y · 수집'], ['N', 'N · 미수집']]} onChange={setYn} disabled={readOnly} label="수집 여부" />
          </Box>
          <TextField id="rd-4" label="비고" value={note} onChange={e => setNote(e.target.value)} disabled={readOnly} multiline minRows={2} variant="outlined" size="small" />
          <Box style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 12 }}>
            <TextField id="rd-5"
              label={addNames.length ? '근거 티켓 (컬럼 추가 — 필수)' : '근거 티켓 (선택)'} placeholder="DPD-12345" value={ticket} onChange={e => setTicket(e.target.value.trim())}
              disabled={readOnly} error={ticketBad} helperText={ticketBad ? '티켓 키 형식이 아닙니다 — 예: DPD-13791' : undefined} variant="outlined" size="small"
            />
            <TextField id="rd-6"
              label="변경 사유 (필수)" placeholder="예: 원천 테이블 폐기로 수집 중단" value={reason} onChange={e => setReason(e.target.value)}
              disabled={readOnly} variant="outlined" size="small"
            />
          </Box>
          <Box display="flex" alignItems="center" style={{ gap: 12 }}>
            <Typography variant="caption" color="textSecondary" style={{ flex: 1 }}>{summary}</Typography>
            <Button variant="contained" color="primary" onClick={save} disabled={saveDisabled}>저장</Button>
          </Box>
        </Box>
      </Paper>

      <ManifestPrCard key={`pr-${table.id}-${table.version}`} table={table} readOnly={readOnly} onChanged={onReload} />
      <OmHistoryCard key={`om-${table.id}`} tableId={table.id} />
      <RegistryColumns
        key={`${table.id}-${table.version}`} table={table} readOnly={readOnly} drop={drop} add={add} update={colEdit}
        onEdit={(n, u) => setColEdit(s => (u ? { ...s, [n]: u } : Object.fromEntries(Object.entries(s).filter(([k]) => k !== n))))}
        onToggleDrop={n => setDrop(d => (d.includes(n) ? d.filter(x => x !== n) : [...d, n]))}
        onToggleAdd={(n, t) => setAdd(a => (n in a ? Object.fromEntries(Object.entries(a).filter(([k]) => k !== n)) : { ...a, [n]: t }))}
      />
      {/* version 이 바뀌면(수정 직후) 이력도 다시 불러온다 */}
      <RegistryHistory key={`${table.id}-${table.version}`} tableId={table.id} />
    </Box>
  );
};
